import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api/client.ts';
import { keys, useMe } from '../api/hooks.ts';
import { Button, ErrorNote, Input, useToast } from '../components/ui/index.tsx';
import { useTheme } from '../layout/AppShell.tsx';
import { Segmented } from '../components/ui/index.tsx';
import { useLogout } from '../features/auth/logout.ts';
import { OAUTH_ERRORS, ProviderMark, startUrl, useProviders } from '../features/auth/Providers.tsx';
import type { ProviderId } from '../api/types.ts';
import { t } from '../i18n/index.ts';

export function AccountPage() {
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const [theme, setTheme] = useTheme();
  const [name, setName] = useState(me.user.displayName);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [error, setError] = useState<unknown>(null);
  const [pwError, setPwError] = useState<unknown>(null);
  const [saving, setSaving] = useState<'name' | 'pw' | 'verify' | ProviderId | null>(null);
  const [params] = useSearchParams();
  const providers = useProviders().data ?? [];
  const identities = me.identities ?? [];
  const linkedNow = params.get('vinculada');
  const linkError = params.get('error');

  const sendVerification = async () => {
    setSaving('verify');
    try {
      await api.post('/api/auth/verify-email/send');
      toast({ text: t('Te enviamos un enlace a {email}', { email: me.user.email }), tone: 'ok' });
    } catch (err) {
      toast({ text: err instanceof Error ? t(err.message) : t('No se pudo enviar. Inténtalo de nuevo.'), tone: 'error' });
    } finally {
      setSaving(null);
    }
  };
  const unlink = async (id: ProviderId) => {
    setSaving(id);
    try {
      await api.del(`/api/auth/identities/${id}`);
      await qc.invalidateQueries({ queryKey: keys.me });
      toast({ text: t('Cuenta desvinculada'), tone: 'ok' });
    } catch (err) {
      toast({ text: err instanceof Error ? t(err.message) : t('No se pudo desvincular.'), tone: 'error' });
    } finally {
      setSaving(null);
    }
  };

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    setSaving('name');
    setError(null);
    try {
      await api.patch('/api/auth/me', { displayName: name });
      await qc.invalidateQueries({ queryKey: keys.me });
      toast({ text: t('Nombre actualizado'), tone: 'ok' });
    } catch (err) {
      setError(err);
    } finally {
      setSaving(null);
    }
  };
  const savePw = async (e: FormEvent) => {
    e.preventDefault();
    setSaving('pw');
    setPwError(null);
    try {
      await api.post('/api/auth/password', pw);
      setPw({ currentPassword: '', newPassword: '' });
      toast({ text: t('Contraseña cambiada. Cerramos tus otras sesiones.'), tone: 'ok' });
    } catch (err) {
      setPwError(err);
    } finally {
      setSaving(null);
    }
  };
  const logout = useLogout();

  return (
    <main className="mx-auto max-w-xl px-5 pb-20 pt-8">
      <Link to="/arboles" className="inline-flex items-center gap-1.5 text-[14.5px] text-ink-soft hover:text-ink">
        <ArrowLeft className="size-4" /> {t('Mis árboles')}
      </Link>
      <h1 className="mt-4 text-[32px]">{t('Mi cuenta')}</h1>
      <p className="mt-1 text-ink-soft">{me.user.email}</p>
      {me.user.emailVerified === false && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-ochre/40 bg-ochre/10 px-4 py-3 text-[14.5px]">
          <span className="flex-1">{t('Aún no confirmaste tu correo. Te enviamos un enlace al registrarte.')}</span>
          <Button size="sm" variant="secondary" loading={saving === 'verify'} onClick={() => void sendVerification()}>{t('Enviar otro enlace')}</Button>
        </div>
      )}

      <section className="mt-8 rounded-3xl border border-line bg-surface p-6">
        <h2 className="text-xl">{t('Cómo te ven tus familiares')}</h2>
        <form onSubmit={saveName} className="mt-4 flex flex-col gap-4">
          <Input label={t('Nombre visible')} value={name} maxLength={120} required onChange={(e) => setName(e.target.value)} />
          <ErrorNote error={error} />
          <Button type="submit" loading={saving === 'name'} className="self-start">{t('Guardar nombre')}</Button>
        </form>
      </section>

      {(providers.length > 0 || identities.length > 0) && (
        <section className="mt-5 rounded-3xl border border-line bg-surface p-6">
          <h2 className="text-xl">{t('Formas de entrar')}</h2>
          <p className="mt-1 text-[14.5px] text-ink-soft">{t('Vincula tu cuenta de Google u otro proveedor para entrar sin contraseña.')}</p>
          {linkedNow && <p className="mt-3 rounded-2xl bg-ok/10 px-4 py-3 text-[14.5px] text-ok">{t('Cuenta vinculada.')}</p>}
          {linkError && <p role="alert" className="mt-3 rounded-2xl bg-danger/8 px-4 py-3 text-[14.5px] text-danger">{OAUTH_ERRORS[linkError] ?? OAUTH_ERRORS.oauth_failed}</p>}
          <ul className="mt-4 flex flex-col divide-y divide-line">
            {providers.map((p) => {
              const linked = identities.find((i) => i.provider === p.id);
              return (
                <li key={p.id} className="flex flex-wrap items-center gap-3 py-3">
                  <ProviderMark id={p.id} />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{p.label}</span>
                    <span className="block truncate text-[13.5px] text-ink-faint">{linked ? (linked.email ?? t('Vinculada')) : t('Sin vincular')}</span>
                  </span>
                  {linked ? (
                    <Button size="sm" variant="ghost" loading={saving === p.id} onClick={() => void unlink(p.id)}>{t('Desvincular')}</Button>
                  ) : (
                    <a href={startUrl(p.id, { link: true })} className="inline-flex h-8 items-center rounded-full border border-line px-3 text-sm font-medium hover:bg-surface-2">
                      {t('Vincular')}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="mt-5 rounded-3xl border border-line bg-surface p-6">
        <h2 className="text-xl">{t('Contraseña')}</h2>
        {me.user.hasPassword === false ? (
          <p className="mt-2 text-[14.5px] text-ink-soft">
            {t('Entras con {providers}. Si también quieres una contraseña, cierra sesión y usa «Olvidé mi contraseña»: te llegará un enlace a {email}.', {
              providers: identities.map((i) => providers.find((p) => p.id === i.provider)?.label ?? i.provider).join(t(' o ')),
              email: me.user.email,
            })}
          </p>
        ) : (
        <form onSubmit={savePw} className="mt-4 flex flex-col gap-4">
          <Input label={t('Contraseña actual')} type="password" autoComplete="current-password" required value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
          <Input label={t('Contraseña nueva')} type="password" autoComplete="new-password" minLength={10} required value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
          <ErrorNote error={pwError} />
          <Button type="submit" loading={saving === 'pw'} className="self-start">{t('Cambiar contraseña')}</Button>
        </form>
        )}
      </section>

      <section className="mt-5 rounded-3xl border border-line bg-surface p-6">
        <h2 className="text-xl">{t('Apariencia')}</h2>
        <div className="mt-4">
          <Segmented label={t('Tema')} value={theme} onChange={setTheme} options={[{ value: 'system', label: t('Automático') }, { value: 'light', label: t('Claro') }, { value: 'dark', label: t('Oscuro') }]} />
        </div>
      </section>

      <Button variant="secondary" className="mt-8" onClick={logout}>{t('Cerrar sesión')}</Button>
    </main>
  );
}
